import { useList, useNavigation } from "@refinedev/core";

import type { RuleRun } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "@/components/ui/icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  EmptyState,
  ErrorState,
  TableSkeletonRows,
  TableStateRow,
} from "@/components/ui/states";
import { StatusBadge } from "@/components/app/StatusBadge";
import { formatDateTime } from "@/lib/format";

/**
 * Recent rule_runs for a single rule. Read-only relation panel rendered inside
 * the rule show page's "Run History" Inspector section (E0 — no Card; the
 * section heading provides structure). Leads with status/started/finished —
 * never ids — and uses the canonical loading / empty / error states. Each row
 * links to the rule-run detail view.
 */
export function RuleRunHistory({ ruleId }: { ruleId: string }) {
  const { show } = useNavigation();
  const { data, isLoading, isError, error, refetch } = useList<RuleRun>({
    resource: "rule-runs",
    pagination: { pageSize: 25, mode: "server" },
    filters: [{ field: "rule_id", operator: "eq", value: ruleId }],
    sorters: [{ field: "started_at", order: "desc" }],
  });

  const runs = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void refetch();
          }}
        >
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
          Refresh
        </Button>
      </div>
      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Finished</TableHead>
              <TableHead>Guard</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows columns={5} rows={3} />
            ) : isError ? (
              <TableStateRow columns={5}>
                <ErrorState error={error} onRetry={() => void refetch()} />
              </TableStateRow>
            ) : runs.length === 0 ? (
              <TableStateRow columns={5}>
                <EmptyState
                  icon="ruleRuns"
                  message="No runs yet"
                  hint="This rule has not been evaluated yet."
                />
              </TableStateRow>
            ) : (
              runs.map((run) => (
                <TableRow
                  key={run.id}
                  className="cursor-pointer"
                  onClick={() => show("rule-runs", run.id)}
                >
                  <TableCell>
                    <StatusBadge status={run.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(run.started_at)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {run.finished_at ? formatDateTime(run.finished_at) : "—"}
                  </TableCell>
                  <TableCell className="max-w-[16rem] truncate font-mono text-xs text-muted-foreground">
                    {run.guard_result ?? "—"}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => show("rule-runs", run.id)}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default RuleRunHistory;
